import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TelegramService } from '../services/telegram/telegram.service';

@Injectable()
export class TelegramAuthInterceptor implements HttpInterceptor {
  private telegramService = inject(TelegramService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip interceptor for translation files and other static assets
    if (req.url.startsWith('/i18n/') || req.url.includes('.json')) {
      console.log('[ACFE] TelegramAuthInterceptor: skipping (static asset)', req.url);
      return next.handle(req);
    }

    // Wait for initData to be ready
    if (!environment.production) {
      console.log('[ACFE] TelegramAuthInterceptor: skipping (dev mode) for', req.url);
      return next.handle(req);
    }

    console.log('[ACFE] TelegramAuthInterceptor: waiting for initData', {
      url: req.url,
      method: req.method,
      apiUrl: environment.apiUrl,
    });
    return this.telegramService.getInitData$().pipe(
      filter((data) => data !== null),
      take(1),
      timeout(10000),
      switchMap((storedInitData) => {
        console.log('[ACFE] TelegramAuthInterceptor: initData ready, building request', {
          url: req.url,
          initDataLength: storedInitData?.length ?? 0,
        });
        if (!storedInitData) {
          return throwError(
            () =>
              new HttpErrorResponse({
                error: 'Telegram initData is required but not available',
                status: 401,
                statusText: 'Unauthorized - Missing Telegram initData',
              }),
          );
        }

        const headers: Record<string, string> = {
          Authorization: `tma ${storedInitData}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        };

        const hasBody = req.method !== 'GET' && req.method !== 'DELETE';
        if (hasBody && !req.headers.has('Content-Type')) {
          headers['Content-Type'] = 'application/json';
        }

        const clonedReq = req.clone({
          setHeaders: headers,
          withCredentials: true,
        });

        console.log('[ACFE] TelegramAuthInterceptor: sending request', {
          url: clonedReq.url,
          method: clonedReq.method,
          withCredentials: clonedReq.withCredentials,
          headerKeys: Object.keys(headers),
        });

        return next.handle(clonedReq);
      }),
      catchError((err) => {
        console.error('[ACFE] TelegramAuthInterceptor: error', {
          url: req.url,
          error: err?.message ?? String(err),
          status: (err as HttpErrorResponse)?.status,
        });
        return throwError(() => err);
      }),
    );
  }
}
